// src/pages/AdminStats.js
import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Spinner, Alert
} from 'react-bootstrap';
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const AdminStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const { t } = useTranslation();

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/admin/stats');
            setStats(response.data.data);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.loadingError'));
            console.error('Error fetching statistics:', err);
        } finally {
            setLoading(false);
        }
    };

    // Форматирование времени для графика
    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        return new Date(timestamp).toLocaleTimeString();
    };

    // Цвета для графиков
    const COLORS = ['#6667f7', '#ff59f8', '#05cd99', '#ffbb33', '#49a7ff'];

    if (loading) {
        return (
            <Container className="my-4 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">{t('common.loading')}</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="my-4">
                <Alert variant="danger">
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="my-4">
            <h1 className="mb-4">{t('admin.stats')}</h1>

            <Row className="mb-4">
                <Col md={3}>
                    <Card className="stat-card card-highlight h-100">
                        <Card.Body className="text-center">
                            <h6 className="text-secondary mb-2">{t('admin.totalUsers')}</h6>
                            <h2>{stats?.totalUsers || 0}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card card-highlight h-100">
                        <Card.Body className="text-center">
                            <h6 className="text-secondary mb-2">{t('admin.totalFlightSheets')}</h6>
                            <h2>{stats?.totalFlightSheets || 0}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card card-highlight h-100">
                        <Card.Body className="text-center">
                            <h6 className="text-secondary mb-2">{t('admin.activeWorkers')}</h6>
                            <h2>{stats?.activeWorkers || 0}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card card-highlight h-100">
                        <Card.Body className="text-center">
                            <h6 className="text-secondary mb-2">{t('dashboard.totalHashrate')}</h6>
                            <h2 className="text-primary">{stats?.hashrateFormatted || '0 H/s'}</h2>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={8}>
                    <Card className="shadow-sm h-100">
                        <Card.Header>
                            <h5 className="mb-0">{t('charts.hashrateChart')} (24 {t('charts.hours')})</h5>
                        </Card.Header>
                        <Card.Body>
                            {stats?.hashrateHistory?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart
                                        data={stats.hashrateHistory}
                                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={formatTime}
                                        />
                                        <YAxis />
                                        <Tooltip
                                            formatter={(value) => [value, t('charts.hashrate')]}
                                            labelFormatter={formatTime}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="hashrate"
                                            stroke="#8884d8"
                                            fill="#8884d8"
                                            fillOpacity={0.3}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center py-5">
                                    <p className="text-muted">{t('charts.noData')}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm h-100">
                        <Card.Header>
                            <h5 className="mb-0">{t('dashboard.shares')} (24 {t('charts.hours')})</h5>
                        </Card.Header>
                        <Card.Body>
                            {stats?.shares ? (
                                <React.Fragment>
                                    <div className="text-center">
                                        <h3>
                                            {stats.shares.acceptanceRate}
                                        </h3>
                                        <p className="text-muted">{t('dashboard.acceptanceRate')}</p>
                                    </div>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: t('dashboard.accepted'), value: stats.shares.accepted },
                                                    { name: t('dashboard.rejected'), value: stats.shares.rejected }
                                                ]}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            >
                                                <Cell fill="#05cd99" />
                                                <Cell fill="#ff4e6e" />
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </React.Fragment>
                            ) : (
                                <div className="text-center py-5">
                                    <p className="text-muted">{t('charts.noData')}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <Card className="shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">{t('admin.topUsersByHashrate')}</h5>
                        </Card.Header>
                        <Card.Body>
                            {stats?.topUsersByHashrate?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart
                                        data={stats.topUsersByHashrate}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="username" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="hashrate" name={t('dashboard.hashrate')} fill="#6667f7" />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center py-5">
                                    <p className="text-muted">{t('charts.noData')}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AdminStats;